import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator } from "lucide-react";

export const SavingsCalculator = () => {
  const [selectedTools, setSelectedTools] = useState({
    resume: false,
    ats: false,
    autoApply: false,
    interview: false,
  });

  const [showResults, setShowResults] = useState(false);

  const toolPrices = {
    resume: 36,
    ats: 49,
    autoApply: 24,
    interview: 20,
  };

  const calculateSavings = () => {
    let total = 0;
    if (selectedTools.resume) total += toolPrices.resume;
    if (selectedTools.ats) total += toolPrices.ats;
    if (selectedTools.autoApply) total += toolPrices.autoApply;
    if (selectedTools.interview) total += toolPrices.interview;
    return total;
  };

  const totalSpending = calculateSavings();
  const loopbaanPrice = 29;
  const monthlySavings = Math.max(0, totalSpending - loopbaanPrice);
  const annualSavings = monthlySavings * 12;

  const handleCalculate = () => {
    setShowResults(true);
  };

  return (
    <Card className="max-w-2xl mx-auto my-20 border-2 border-primary/30 shadow-xl bg-gradient-to-br from-card to-card/50">
      <CardHeader className="text-center pb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Calculator className="w-8 h-8 text-primary" />
          <CardTitle className="text-3xl">See How Much You'll Save</CardTitle>
        </div>
        <CardDescription className="text-base">
          Select the tools you're currently using or considering
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-primary/20">
            <Checkbox
              id="resume"
              checked={selectedTools.resume}
              onCheckedChange={(checked) =>
                setSelectedTools({ ...selectedTools, resume: checked as boolean })
              }
            />
            <label
              htmlFor="resume"
              className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Resume builder (e.g., Teal)
              <span className="ml-auto float-right text-muted-foreground font-semibold">${toolPrices.resume}/mo</span>
            </label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-primary/20">
            <Checkbox
              id="ats"
              checked={selectedTools.ats}
              onCheckedChange={(checked) =>
                setSelectedTools({ ...selectedTools, ats: checked as boolean })
              }
            />
            <label
              htmlFor="ats"
              className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              ATS optimization tools (e.g., Resume Worded)
              <span className="ml-auto float-right text-muted-foreground font-semibold">${toolPrices.ats}/mo</span>
            </label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-primary/20">
            <Checkbox
              id="autoApply"
              checked={selectedTools.autoApply}
              onCheckedChange={(checked) =>
                setSelectedTools({ ...selectedTools, autoApply: checked as boolean })
              }
            />
            <label
              htmlFor="autoApply"
              className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Auto-apply tools (e.g., JobCopilot)
              <span className="ml-auto float-right text-muted-foreground font-semibold">${toolPrices.autoApply}/mo</span>
            </label>
          </div>

          <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-primary/20">
            <Checkbox
              id="interview"
              checked={selectedTools.interview}
              onCheckedChange={(checked) =>
                setSelectedTools({ ...selectedTools, interview: checked as boolean })
              }
            />
            <label
              htmlFor="interview"
              className="flex-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Interview prep tools
              <span className="ml-auto float-right text-muted-foreground font-semibold">${toolPrices.interview}/mo</span>
            </label>
          </div>
        </div>

        <Button 
          onClick={handleCalculate} 
          className="w-full shadow-lg hover:shadow-xl transition-all"
          size="lg"
        >
          Calculate Your Savings
        </Button>

        {showResults && (
          <div className="mt-6 p-6 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border-2 border-primary space-y-4 animate-fade-in shadow-lg">
            <div className="flex justify-between items-center text-lg">
              <span className="font-medium">You're currently spending:</span>
              <span className="font-bold text-destructive text-xl">${totalSpending}/month</span>
            </div>
            <div className="flex justify-between items-center text-lg">
              <span className="font-medium">With Loopbaan.ai Pro:</span>
              <span className="font-bold text-primary text-xl">${loopbaanPrice}/month</span>
            </div>
            <div className="border-t-2 border-primary pt-4">
              <div className="flex justify-between items-center text-xl font-bold mb-2">
                <span>Monthly Savings:</span>
                <span className="text-primary">${monthlySavings}</span>
              </div>
              <div className="flex justify-between items-center text-2xl font-bold">
                <span>Annual Savings:</span>
                <span className="text-primary text-3xl">${annualSavings}</span>
              </div>
            </div>
            {monthlySavings > 0 && (
              <p className="text-sm text-muted-foreground text-center pt-3 bg-background/50 p-3 rounded-lg">
                That's enough to cover {Math.floor(annualSavings / 50)} nice dinners or invest in your career growth
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
